import {Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, Input} from '@angular/core';

import {forkJoin} from 'rxjs';
import {DailyMacrosViewModel} from '../../../shared/viewModels/dailyMeal.viewModel';
import {UsersService} from '../../../shared/services/users.service';
import {NotificationsService} from '../../../shared/services/notification.service';
import {LocalisationService} from '../../../shared/services/localisation.service';
import {ProductsService} from '../../../shared/services/products.service';
import {MasterService, MealWithDailyMealViewModel} from '../../../shared/services/master.service';
import {FormControl} from '@angular/forms';
import {AppLoadingEventsEmitter} from '../../../shared/events/loading.events';
import {ProfilesCreateModalComponent} from '../profiles-create/profiles-create.component';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {BsModalEventsEmitter} from '../../../shared/events/bsModal.events';
import {DailyMacroChangeComponent} from '../daily-macro-change/daily-macro-change.component';

@Component({
    selector: 'app-master-macros',
    templateUrl: './macros.component.html',
    styleUrls: ['./macros.component.scss']
})
export class MacrosComponent implements OnInit {
    @Input() editable = true;
    @Input() notifications = true;
    @Input() id;

    overwrite_all_calories: FormControl = new FormControl('', []);
    overwrite_protein: FormControl = new FormControl('', []);
    overwrite_carbs: FormControl = new FormControl('', []);
    overwrite_fat: FormControl = new FormControl('', []);

    daily_overwrites: DailyMacrosViewModel[] = [];
    dailyMeals: MealWithDailyMealViewModel[] = [];
    daileMealPercentageOverwrites = {};

    bsModalRef: BsModalRef;

    constructor(
        private readonly usersService: UsersService,
        private readonly notificationService: NotificationsService,
        private readonly localisationService: LocalisationService,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly productsService: ProductsService,
        private readonly masterService: MasterService,
        private readonly appIsLoadingEventsEmitter: AppLoadingEventsEmitter,
        private readonly bsModalService: BsModalService,
        private readonly bsModalEventsEmitter: BsModalEventsEmitter,
    ) {
    }

    ngOnInit() {
        this.appIsLoadingEventsEmitter.broadcastAppIsLoading(true);

        forkJoin(
            this.masterService.getUserProfile(this.id),
            this.masterService.getOverwrites(this.id),
            this.masterService.getMealsWithDailyMeals(this.id),
        ).subscribe(
            ([userProfile, overwrites, meals_with_daily_meal]) => {
                this.daily_overwrites = overwrites.user_overwrite_macros;

                overwrites.user_overwrite_macros.forEach(om => {
                    if (!this.daileMealPercentageOverwrites[om.id]) {
                        this.daileMealPercentageOverwrites[om.id] = [];
                    }
                });

                overwrites.meal_overwrite.forEach(mo => {
                    if (!this.daileMealPercentageOverwrites[mo.macroId]) {
                        this.daileMealPercentageOverwrites[mo.macroId] = [];
                    }

                    this.daileMealPercentageOverwrites[mo.macroId].push(mo);
                });

                meals_with_daily_meal
                    .map(m => m.dailyMeal)
                    .forEach(dm => {
                        if (this.dailyMeals.findIndex(d => d.id === dm.id) === -1) {
                            this.dailyMeals.push(dm);
                        }
                    });

                this.overwrite_all_calories.setValue(userProfile.overwrite_all_calories);
                this.overwrite_protein.setValue(userProfile.overwrite_protein);
                this.overwrite_carbs.setValue(userProfile.overwrite_carbs);
                this.overwrite_fat.setValue(userProfile.overwrite_fat);

                this.changeDetectorRef.markForCheck();
                this.appIsLoadingEventsEmitter.broadcastAppIsLoading(false);
            }
        );
    }

    updateProfile() {
        this.appIsLoadingEventsEmitter.broadcastAppIsLoading(true);

        forkJoin(
            this.usersService
                .updateMasterUserProfile(
                    this.id,
                    {
                        overwrite_all_calories: this.overwrite_all_calories.value,
                        overwrite_protein: this.overwrite_protein.value,
                        overwrite_carbs: this.overwrite_carbs.value,
                        overwrite_fat: this.overwrite_fat.value,
                    }
                ),
            this.masterService.updateOverwrites(this.id, this.daily_overwrites, this.daileMealPercentageOverwrites)
        ).subscribe(
            ([profile, overwrites]) => {
                this.appIsLoadingEventsEmitter.broadcastAppIsLoading(false);

                this.changeDetectorRef.detectChanges();

                if (this.notifications) {
                    this.notificationService.success(this.localisationService.localise('settings_profile_updated_successfully'));
                }
            }, () => {
                this.appIsLoadingEventsEmitter.broadcastAppIsLoading(false);
            });
    }

    get saveButtonIsDisabled(): boolean {
        return false;
    }

    onDayChange($event, i: number) {
        this.daily_overwrites[i]['day'] = $event.target.value;
        this.changeDetectorRef.markForCheck();
    }

    addNewDailyMacro() {
        const newDay = new DailyMacrosViewModel();
        newDay.id = this.generateNewId();

        console.log(newDay);

        this.daily_overwrites.push(newDay);

        this.daileMealPercentageOverwrites[newDay.id] = [];

        this.changeDetectorRef.markForCheck();
    }

    private generateNewId() {
        const id = Math.ceil(Math.random() * 99999999999);

        if (this.daily_overwrites.findIndex(d => d.id === id) !== -1) {
            return this.generateNewId();
        }

        return id;
    }

    removeDailyMacro(i: number) {
        this.daily_overwrites.splice(i, 1);
        this.changeDetectorRef.markForCheck();
    }

    duplicateDailyMacro(i: number) {
        const id = this.generateNewId();
        this.daily_overwrites.push({
            id: id,
            day: this.daily_overwrites[i].day,
            daily_meal_id: this.daily_overwrites[i].daily_meal_id,
            overwrite_all_calories: this.daily_overwrites[i].overwrite_all_calories,
            overwrite_carbs: this.daily_overwrites[i].overwrite_carbs,
            overwrite_fat: this.daily_overwrites[i].overwrite_fat,
            overwrite_protein: this.daily_overwrites[i].overwrite_protein,
        });

        this.daileMealPercentageOverwrites[id] = [];

        this.changeDetectorRef.markForCheck();
    }

    changeDailyMacroCalories($event, i: number) {
        this.daily_overwrites[i].overwrite_all_calories = $event.target.value;
        this.changeDetectorRef.markForCheck();
    }

    changeDailyMacroProtein($event, i: number) {
        this.daily_overwrites[i].overwrite_protein = $event.target.value;
        this.changeDetectorRef.markForCheck();
    }

    changeDailyMacroCarbs($event, i: number) {
        this.daily_overwrites[i].overwrite_carbs = $event.target.value;
        this.changeDetectorRef.markForCheck();
    }

    changeDailyMacroFat($event, i: number) {
        this.daily_overwrites[i].overwrite_fat = $event.target.value;
        this.changeDetectorRef.markForCheck();
    }

    onDailyMealChange($event, i: number) {
        if ($event.target.value.length <= 0) {
            this.daily_overwrites[i].daily_meal_id = null;
        } else {
            // tslint:disable-next-line:radix
            this.daily_overwrites[i].daily_meal_id = parseInt($event.target.value);
        }

        this.changeDetectorRef.markForCheck();
    }

    openChangeMealPercentage(index: number) {
        this.bsModalRef = this.bsModalService.show(DailyMacroChangeComponent, {
            backdrop: true
        });

        this.bsModalRef.content.macrosComponent = this;
        this.bsModalRef.content.day = this.getDayName(this.daily_overwrites[index]['day']);
        this.bsModalRef.content.dailyMeal = this.getDailyMealName(this.daily_overwrites[index]['daily_meal_id']);
        this.bsModalRef.content.masterUserId = this.id;
        this.bsModalRef.content.dailyOverwrite = this.daily_overwrites[index];
        this.bsModalRef.content.dailyOverwriteIndex = index;

        if (this.daileMealPercentageOverwrites[this.daily_overwrites[index]['id']]) {
            this.bsModalRef.content.mealOverwrites = this.daileMealPercentageOverwrites[this.daily_overwrites[index]['id']].sort((a, b) => {
                return a.meal.id - b.meal.id;
            });
        }

        this.bsModalEventsEmitter.broadcastBsModalStateChanged(this.bsModalRef);
    }

    private getDailyMealName(id: number): string {
        for (const dm of this.dailyMeals) {
            if (dm.id === id) {
                return dm.title;
            }
        }

        return '';
    }

    private getDayName(day: string): string {
        switch (day) {
            case 'MON':
                return 'Понеделник';
            case 'TUE':
                return 'Вторник';
            case 'WED':
                return 'Сряда';
            case 'THU':
                return 'Четвъртък';
            case 'FRI':
                return 'Петък';
            case 'SAT':
                return 'Събота';
            case 'SUN':
                return 'Неделя';
            default:
                return 'None';
        }
    }
}
